/**
 * Context-aware starter suggestions for Inky's empty state. They are seeded into the
 * composer for the user to adjust and send; they are never sent automatically.
 */

import { oneLine } from "./inkyPersona";

/**
 * Build 3-4 suggestions for what is on screen.
 * @param {{kind: string, project: object|null, document: object|null, template: object|null, session: object|null, customer: object|null}} active - Active entities.
 * @param {{isEmptyDocument?: boolean, hasSelection?: boolean, isDeck?: boolean}} [editorState] - Open editor hints
 *   (`isDeck`: the deck template editor is open, see surfaceBridge.isDeckEditorOpen).
 * @returns {Array<{label: string, prompt: string}>} Suggestions.
 */
export const buildSuggestions = (active, editorState = {}) => {
  const project = active?.project;
  const projectName = project ? oneLine(project.name, 40) : null;

  if (active?.kind === "document") {
    if (editorState.isEmptyDocument) {
      return [
        { label: "Draft from requirements", prompt: `Draft this document from ${projectName ? `${projectName}'s` : "the project's"} requirements and recent meetings.` },
        { label: "Outline first", prompt: "Give this document a clear section outline before we write it." },
        { label: "What should it cover?", prompt: "Based on the project's requirements, what should this document cover?" },
      ];
    }
    return [
      ...(editorState.hasSelection ? [{ label: "Improve selection", prompt: "Tighten the selected text and make it clearer." }] : []),
      { label: "Check coverage", prompt: "Which project requirements does this document not cover yet?" },
      { label: "Tighten the writing", prompt: "Make the whole document more concise without losing any facts." },
      { label: "Add a risks section", prompt: "Add a risks and mitigations section based on the meeting notes." },
    ].slice(0, 4);
  }

  // The deck template editor (PowerPoint master): Inky reads it and suggests wording; it cannot
  // edit it yet, so no suggestion asks for an edit to be applied.
  if (active?.kind === "template" && editorState.isDeck) {
    return [
      { label: "Review the suggestions", prompt: "Which suggested AI fields in this deck should I check before accepting them, and why?" },
      { label: "Explain this slide", prompt: "What is this slide for, and what will each AI field on it write?" },
      { label: "Sharper prompts", prompt: "Suggest clearer prompts for the AI fields on this slide: what to cover, how long, and which evidence to use." },
      { label: "Check fixed text", prompt: "Which boxes in this deck are fixed text, and should any of them be AI fields instead?" },
    ];
  }

  // The template editor's quick actions live here only (the editor page has no separate list).
  if (active?.kind === "template") {
    return [
      { label: "Refine instructions", prompt: "Make every AI field instruction in this template more specific: what to cover, how much detail and which evidence to use." },
      { label: "Add AI fields", prompt: "Turn the sections that need project-specific content into AI fields." },
      { label: "Fix placeholders", prompt: "Find vague, duplicated or broken AI fields in this template and fix them." },
      { label: "Add a section", prompt: "Suggest one important section this template is missing and add it with an AI field." },
    ];
  }

  if (active?.kind === "session") {
    const name = active.session ? oneLine(active.session.name, 40) : "this meeting";
    return [
      { label: "Summarize", prompt: `Summarize ${name}: key decisions and action items.` },
      { label: "Requirements discussed", prompt: `What requirements came out of ${name}?` },
      { label: "Follow-up email", prompt: `Draft a short follow-up email for ${name} with the action items.` },
    ];
  }

  if (active?.kind === "project" && project) {
    return [
      { label: "Project status", prompt: `Give me a status summary of ${projectName} from its meetings and requirements.` },
      { label: "Top requirements", prompt: `What are the highest-priority requirements in ${projectName}?` },
      { label: "Generate a document", prompt: `Generate a document for ${projectName}.` },
      { label: "Open questions", prompt: `What is still unclear or undecided in ${projectName}?` },
    ];
  }

  if (active?.kind === "customer" && active.customer) {
    const name = oneLine(active.customer.name, 40);
    return [
      { label: "Customer overview", prompt: `Give me an overview of ${name}: projects, recent meetings and open actions.` },
      { label: "Open action items", prompt: `List the open action items for ${name}.` },
      { label: "New project", prompt: `Create a new project for ${name}.` },
    ];
  }

  return [
    { label: "What changed recently?", prompt: "What happened in my most recent meetings?" },
    { label: "Generate a document", prompt: "Generate a document from one of my projects." },
    { label: "Find a requirement", prompt: "Find requirements about " },
    { label: "Open a project", prompt: "Open the project " },
  ];
};
